import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SequenceStep } from "./SequenceStep";
import { Step, SequenceTemplate } from "./types";
import { useState } from "react";
import { Plus } from "lucide-react";

interface EditSequenceDialogProps {
  sequence: SequenceTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (sequence: SequenceTemplate, steps: Step[]) => Promise<void>;
}

export const EditSequenceDialog = ({ 
  sequence, 
  open, 
  onOpenChange,
  onSave 
}: EditSequenceDialogProps) => {
  const [name, setName] = useState(sequence?.name || "");
  const [description, setDescription] = useState(sequence?.description || "");
  const [steps, setSteps] = useState<Step[]>(sequence?.steps || []);

  const handleSave = async () => {
    if (!sequence) return;
    await onSave({ ...sequence, name, description }, steps);
    onOpenChange(false);
  };

  const handleStepUpdate = (index: number, field: string, value: any) => {
    const newSteps = [...steps];
    const step = { ...newSteps[index] };
    
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      step[parent] = { ...step[parent], [child]: value };
    } else {
      step[field] = value;
    }
    
    newSteps[index] = step;
    setSteps(newSteps);
  };

  const handleAddStep = () => {
    const newStep: Step = {
      step_number: steps.length + 1,
      channel: 'email',
      content_template: {
        subject: '',
        body: ''
      },
      delay_hours: 0,
      subject_type: 'static',
      auto_trigger: true
    };
    setSteps([...steps, newStep]);
  };

  const handleRemoveStep = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Sequence</DialogTitle>
          <DialogDescription>
            Update the sequence details and steps below
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="name">Name</label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="description">Description</label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Sequence Steps</h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleAddStep}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Step
              </Button>
            </div>
            {steps.map((step, index) => (
              <SequenceStep
                key={index}
                step={step}
                onRemove={() => handleRemoveStep(index)}
                onUpdate={(field, value) => handleStepUpdate(index, field, value)}
              />
            ))}
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};